import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { aneelCaseParams, fetchAneelCases } from '@/services/aneel';
import {
  ANEEL_OUTCOME_FILTER_ANY,
  ANEEL_OUTCOME_FILTER_NONE,
  type AneelCaseRow,
  type AneelFilterOption,
} from '@/services/types';

const PAGE_SIZE = 50;

/** A full CNPJ, the only key `/aneel/e/:cnpj` accepts. */
const CNPJ_DIGITS = 14;

/**
 * Every infraction notice ANEEL has issued, filtered.
 *
 * Two decisions on this screen are not cosmetic.
 *
 * **It does not default to decided cases.** CADE's list does, because its
 * registry rows are empty in every column and the unfiltered first page reads as
 * a broken screen. ANEEL's undecided cases are not empty — they carry a number, a
 * date, a company, a nature, an agency, a penalty band and a charged amount. Only
 * the outcome is missing. Hiding four fifths of the corpus by default would
 * misrepresent what ANEEL enforcement is.
 *
 * **The final-amount column is labelled per row, not per column.** ANEEL carries
 * the charged amount into the final-amount column while a case is undecided, so
 * the same number means "what stood" on one row and "what was demanded" on the
 * next. A column header cannot say that; the cell has to.
 *
 * **Every filter seeds from the URL.** The appeal record, the overview, the
 * collection screen and the company profile link here with a filter already
 * applied, and a list that ignored the query string would open on the whole
 * corpus under a link that promised one nature's rulings. The URL is read
 * once, on mount; the controls own the state from then on. `entity` has no
 * select -- a CNPJ is typed nowhere on this page -- so it renders as a chip
 * the reader can clear, never as an invisible narrowing.
 *
 * **Batches, not pages.** Rows accumulate as the reader scrolls, so a list of
 * a company's or a nature's notices can be read top to bottom and handed on
 * as one export. `keepPreviousData` keeps the previous total on screen while
 * a narrowed query is in flight, and `data-stale` says so, because a stale
 * figure and an unchanged one are otherwise byte-identical.
 */
export function AneelCasesPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [nature, setNature] = useState(seed.get('nature') ?? '');
  const [agency, setAgency] = useState(seed.get('agency') ?? '');
  const [trajectory, setTrajectory] = useState(seed.get('trajectory') ?? '');
  const [band, setBand] = useState(seed.get('band') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [firstOutcome, setFirstOutcome] = useState(seed.get('first_outcome') ?? '');
  const [boardOutcome, setBoardOutcome] = useState(seed.get('board_outcome') ?? '');
  const [creditStatus, setCreditStatus] = useState(seed.get('credit_status') ?? '');
  const [penaltyType, setPenaltyType] = useState(seed.get('penalty_type') ?? '');
  const [entity, setEntity] = useState(seed.get('entity') ?? '');

  const filters = {
    query,
    nature: nature || null,
    agency: agency || null,
    trajectory: trajectory || null,
    band: band || null,
    year: year ? Number(year) : null,
    first_outcome: firstOutcome || null,
    board_outcome: boardOutcome || null,
    credit_status: creditStatus || null,
    entity: entity || null,
    penalty_type: penaltyType || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['aneel', 'cases', filters],
    queryFn: ({ pageParam }) => fetchAneelCases({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    // Driven by the server's own total, never by "the last page came back
    // full": a result set that is an exact multiple of PAGE_SIZE would
    // otherwise leave a button promising rows that do not exist.
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  // Every panel above the table reads corpus-level figures that are identical
  // on each page, so they come from the first one. Only the ROWS accumulate.
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];

  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  // A filter change replaces the query key, which drops every accumulated
  // batch at once; nothing here has to reset a page offset.
  const narrow = <T,>(set: (value: T) => void) => (value: T) => set(value);

  const options = data?.options ?? {};

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:cases.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:cases.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:cases.explainer" />

      <div className="flex justify-end">
        {/* The SAME params the screen queried with, minus the page bounds. A
            separate export query is how a spreadsheet starts disagreeing with
            the page it came from. */}
        <ExportButton path={`/api/v1/aneel/export/cases?${aneelCaseParams(filters).toString()}`} />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="aneel-case-query" className="text-xs font-medium text-foreground">
            {t('aneel:cases.search.label')}
          </label>
          <input
            id="aneel-case-query"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('aneel:cases.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
          <p className="text-xs text-muted-foreground">{t('aneel:cases.search.hint')}</p>
        </div>

        <Select
          id="aneel-trajectory"
          label={t('aneel:cases.filters.trajectory')}
          any={t('aneel:cases.filters.any')}
          value={trajectory}
          onChange={narrow(setTrajectory)}
          options={options.trajectories}
          render={(option) => t(`aneel:overview.trajectories.${option.value}`, option.value)}
        />
        <Select
          id="aneel-nature"
          label={t('aneel:cases.filters.nature')}
          any={t('aneel:cases.filters.any')}
          value={nature}
          onChange={narrow(setNature)}
          options={options.natures}
        />
        <Select
          id="aneel-agency"
          label={t('aneel:cases.filters.agency')}
          any={t('aneel:cases.filters.any')}
          value={agency}
          onChange={narrow(setAgency)}
          options={options.agencies}
        />
        <Select
          id="aneel-band"
          label={t('aneel:cases.filters.band')}
          any={t('aneel:cases.filters.any')}
          value={band}
          onChange={narrow(setBand)}
          options={options.bands}
        />
        <Select
          id="aneel-year"
          label={t('aneel:cases.filters.year')}
          any={t('aneel:cases.filters.any')}
          value={year}
          onChange={narrow(setYear)}
          options={options.years}
        />
        {/* Two filters, one per instance, because the two are two records: a
            case can be ruled on by the Diretoria with nothing captured at first
            instance. The ANY/NONE literals arrive from the API as options,
            counted over the same population as the codes, and are labelled here. */}
        <Select
          id="aneel-first-outcome"
          label={t('aneel:cases.filters.firstOutcome')}
          any={t('aneel:cases.filters.any')}
          value={firstOutcome}
          onChange={narrow(setFirstOutcome)}
          options={options.first_outcomes}
          render={outcomeLabel(t)}
        />
        <Select
          id="aneel-board-outcome"
          label={t('aneel:cases.filters.boardOutcome')}
          any={t('aneel:cases.filters.any')}
          value={boardOutcome}
          onChange={narrow(setBoardOutcome)}
          options={options.board_outcomes}
          render={outcomeLabel(t)}
        />
        {/* Case grain: each option's count is the number of CASES carrying a
            collection row in that state, which is what the filter returns and
            what the collection screen's cross-tab cell says. */}
        <Select
          id="aneel-credit-status"
          label={t('aneel:cases.filters.creditStatus')}
          any={t('aneel:cases.filters.any')}
          value={creditStatus}
          onChange={narrow(setCreditStatus)}
          options={options.credit_statuses}
        />
        <Select
          id="aneel-penalty-type"
          label={t('aneel:cases.filters.penaltyType')}
          any={t('aneel:cases.filters.any')}
          value={penaltyType}
          onChange={narrow(setPenaltyType)}
          options={options.penalty_types}
        />

        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('aneel:cases.search.submit')}
        </button>

        {entity ? (
          // The one filter with no control of its own. It arrives from a
          // company profile's "every notice" link, and a list silently
          // narrowed to one CNPJ would read as the corpus.
          <p
            className="flex basis-full items-center gap-2 text-xs text-foreground"
            data-entity-filter={entity}
          >
            <span>{t('aneel:cases.entityFilter', { cnpj: entity })}</span>
            <button
              type="button"
              onClick={() => setEntity('')}
              className="rounded-md border px-2 py-0.5 text-xs text-muted-foreground"
            >
              {t('aneel:cases.clearEntity')}
            </button>
          </p>
        ) : null}
      </form>

      {error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <SkeletonLoader rows={8} />
      ) : (
        <div className="space-y-4">
          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:cases.axes.title')}
            </h2>
            <Answer answer={data.status_axes} help={t('aneel:cases.axes.help')}>
              {(axes) => (
                <div className="space-y-3">
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {(
                      [
                        ['decided', axes.decided],
                        ['decisionRecorded', axes.decision_recorded],
                        ['closed', axes.closed],
                        ['collectionLinked', axes.collection_linked],
                      ] as const
                    ).map(([key, value]) => (
                      <div key={key} className="rounded-md border bg-background p-3">
                        <dt className="text-xs text-muted-foreground">
                          {t(`aneel:cases.axes.${key}`)}
                        </dt>
                        <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                          {number.format(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  {/* The disagreements, stated as sentences. They are the point of
                      the panel: without them four counts side by side read as four
                      views of the same milestone. */}
                  <ul className="space-y-1 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                    <li>
                      {t('aneel:cases.axes.recordedButUndecided', {
                        count: axes.recorded_but_undecided,
                        n: number.format(axes.recorded_but_undecided),
                      })}
                    </li>
                    <li>
                      {t('aneel:cases.axes.closedButUndecided', {
                        n: number.format(axes.closed_but_undecided),
                        closed: number.format(axes.closed),
                      })}
                    </li>
                    <li>
                      {t('aneel:cases.axes.collectedButUndecided', {
                        n: number.format(axes.collected_but_undecided),
                        linked: number.format(axes.collection_linked),
                      })}
                    </li>
                  </ul>
                </div>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="aneel:cases.guide.axes" />
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('aneel:cases.concentration.title')}
            </h2>
            <Answer answer={data.concentration} help={t('aneel:cases.concentration.help')}>
              {(value) => (
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {value.bands.map((entry) => (
                    <div key={entry.label} className="rounded-md border bg-background p-3">
                      <dt className="text-xs text-muted-foreground">
                        {t('aneel:cases.concentration.band', { band: entry.label })}
                      </dt>
                      <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                        {number.format(entry.entities)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </Answer>
          </section>

          {/* Its own element, with the raw total and a staleness flag on it.
              `keepPreviousData` keeps the PREVIOUS query's total on screen for
              the whole of the next fetch — deliberately, so the figure does not
              blink — and the rendered sentence gives no way to tell which query
              it describes. A reader who narrows and sees the old number cannot
              distinguish "the filter changed nothing" from "the filter has not
              landed yet", and neither could a test. */}
          <p
            className="text-sm text-foreground"
            aria-live="polite"
            data-testid="aneel-cases-total"
            data-loaded={items.length}
            data-total={data.total}
            data-stale={String(isPlaceholderData)}
          >
            {t('common:pagination.loaded', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>

          {items.length === 0 ? (
            <EmptyState hint={t('aneel:cases.emptyHint')} />
          ) : (
            <>
              {/* Bounded height with its own scrollbar, so batches grow inside
                  the box rather than pushing the filters and the export several
                  screens above the rows. The sentinel sits INSIDE it, because
                  that is what scrolls; the button beside it stays the real
                  control for a keyboard and for a browser without an observer. */}
              <div
                ref={scrollRef}
                className="max-h-[65vh] overflow-auto rounded-lg border"
                role="region"
                aria-label={t('aneel:cases.tableLabel')}
              >
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.notice')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.date')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.company')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.nature')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.agency')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.band')}</th>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.outcome')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:cases.columns.charged')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('aneel:cases.columns.standing')}
                      </th>
                      <th className="px-3 py-2 font-medium">{t('aneel:cases.columns.state')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <CaseRow key={row.case_key} row={row} currency={currency} />
                    ))}
                  </tbody>
                </table>

                <div ref={sentinelRef} aria-hidden className="h-px" />

                <div className="flex justify-center py-3">
                  {hasNextPage ? (
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={isFetchingNextPage}
                      className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                    >
                      {isFetchingNextPage
                        ? t('common:pagination.loadingMore')
                        : t('common:pagination.loadMore', { count: PAGE_SIZE })}
                    </button>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('common:pagination.allLoaded', { total: number.format(data.total) })}
                    </p>
                  )}
                </div>
              </div>

              <FieldGuide i18nKey="aneel:cases.guide.columns" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The label for an outcome option: a translated code, or the ANY/NONE literal
 * the filter accepts beside the codes -- spelled in `analytics/aneel/appeals.py`
 * and carried by the API as option values.
 */
function outcomeLabel(t: TFunction) {
  return (option: AneelFilterOption) => {
    if (option.value === ANEEL_OUTCOME_FILTER_ANY || option.value === ANEEL_OUTCOME_FILTER_NONE) {
      return t(`aneel:cases.outcomeFilter.${option.value}`);
    }
    return t(`aneel:case.outcomes.${option.value}`, option.value);
  };
}

function CaseRow({ row, currency }: { row: AneelCaseRow; currency: Intl.NumberFormat }) {
  const { t } = useTranslation(['aneel']);

  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className="px-3 py-1.5">
        <Link
          to={`/aneel/c/${encodeURIComponent(row.case_key)}`}
          className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {row.notice_number}
        </Link>
      </td>
      <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{row.notice_date ?? '—'}</td>
      {/* Clamped to two lines rather than left to wrap. A company name and a
          nature string routinely run to three and four lines here, and since a
          row is as tall as its tallest cell, those two columns alone set the
          height of every row on the page. The full value stays in `title` and in
          the accessible name, so this is a presentation limit, not a truncation of
          the data. */}
      <td className="px-3 py-1.5 text-foreground">
        {row.entity_name ? (
          // The profile route takes the full CNPJ and nothing else; a row whose
          // company carries no complete CNPJ stays plain text rather than a
          // link that 404s.
          row.entity_cnpj && row.entity_cnpj.length === CNPJ_DIGITS ? (
            <Link
              to={`/aneel/e/${row.entity_cnpj}`}
              className="line-clamp-2 text-primary underline-offset-4 hover:underline"
              title={row.entity_name}
            >
              {row.entity_name}
            </Link>
          ) : (
            <span className="line-clamp-2" title={row.entity_name}>
              {row.entity_name}
            </span>
          )
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-1.5 text-muted-foreground">
        {row.nature ? (
          <span className="line-clamp-2" title={row.nature}>
            {row.nature}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-1.5 text-muted-foreground">{row.inspecting_agency ?? '—'}</td>
      {/* The bands as the source spells them, one per line: the same strings
          the band filter offers, so a reader can see why a row matched. */}
      <td className="px-3 py-1.5 text-xs text-muted-foreground" data-bands={row.bands.length}>
        {row.bands.length === 0
          ? '—'
          : row.bands.map((band) => (
              <span key={band} className="block" title={band}>
                {band}
              </span>
            ))}
      </td>
      <td className="px-3 py-1.5 text-foreground">
        {row.trajectory
          ? t(`aneel:overview.trajectories.${row.trajectory}`, row.trajectory)
          : t('aneel:overview.trajectories.UNCLASSIFIED')}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
        {row.original_amount === null ? '' : currency.format(row.original_amount)}
      </td>
      {/*
        The most dangerous cell on the screen.

        ANEEL carries the charged amount into the final-amount column while a
        case is undecided, so this number is an OUTCOME on a decided row and a
        DEMAND on an undecided one — identical bytes, opposite meanings. A column
        header cannot carry that distinction, so the cell states it.
      */}
      <td className="px-3 py-1.5 text-right tabular-nums">
        {row.final_amount === null ? (
          ''
        ) : row.decided ? (
          <span className="text-foreground">{currency.format(row.final_amount)}</span>
        ) : (
          <span className="text-muted-foreground" title={t('aneel:cases.chargedNotFinal')}>
            {currency.format(row.final_amount)}{' '}
            <span className="text-[0.65rem] uppercase tracking-wide">
              {t('aneel:cases.chargedShort')}
            </span>
          </span>
        )}
      </td>
      <td className="min-w-[10rem] px-3 py-1.5">
        <StateGlyphs row={row} />
      </td>
    </tr>
  );
}

/**
 * The four axes for one case.
 *
 * Rendered as four independent marks rather than one status word, because they
 * disagree on roughly a fifth of the corpus and any single word would have to
 * pick one of them and discard the rest.
 *
 * Each mark states its own value in an `aria-label`. Without one, the whole cell
 * reads out as four bare nouns — "outcome minuted closed billing" — with nothing
 * to say which of them this case has actually reached, since the distinction is
 * carried entirely by the fill and the border. That is the same information a
 * sighted reader gets from the styling, spelled out.
 */
function StateGlyphs({ row }: { row: AneelCaseRow }) {
  const { t } = useTranslation(['aneel']);
  const axes = [
    ['decided', row.decided],
    ['decisionRecorded', row.decision_recorded],
    ['closed', row.closed],
    ['collectionLinked', row.collection_linked],
  ] as const;

  return (
    <span className="grid grid-cols-2 gap-1">
      {axes.map(([key, on]) => {
        const label = `${t(`aneel:cases.axes.${key}`)}: ${
          on ? t('aneel:case.state.yes') : t('aneel:case.state.no')
        }`;
        return (
          <span
            key={key}
            title={label}
            aria-label={label}
            data-axis={key}
            data-on={String(on)}
            className={
              on
                ? 'truncate rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-center text-[0.65rem] font-medium text-foreground'
                : 'truncate rounded border border-dashed px-1.5 py-0.5 text-center text-[0.65rem] text-muted-foreground/60'
            }
          >
            {t(`aneel:cases.axesShort.${key}`)}
          </span>
        );
      })}
    </span>
  );
}

function Select({
  id,
  label,
  any,
  value,
  onChange,
  options,
  render,
}: {
  id: string;
  label: string;
  any: string;
  value: string;
  onChange: (value: string) => void;
  options?: AneelFilterOption[];
  render?: (option: AneelFilterOption) => string;
}) {
  const { i18n } = useTranslation();
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="max-w-[13rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      >
        {/* Options come from the API, never a literal list. The natures alone are
            38 and the ETL owns that vocabulary, so a hardcoded list would drift
            into offering filters that match nothing — which looks like a working
            filter returning no results. */}
        <option value="">{any}</option>
        {(options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {(render ? render(option) : option.value)} ({number.format(option.cases)})
          </option>
        ))}
      </select>
    </div>
  );
}
